// Website Sync admin page — wired to the real /api/website-sync/* endpoints
// (website-sync.controller.js / schoolWebsiteExtraction.service.js /
// schoolProfileApply.service.js in yellowslate-crm-services).
import React, { useEffect, useRef, useState } from 'react';
import {
  Globe, Search, X, RefreshCw, CheckCircle2, Clock, AlertTriangle,
  ChevronRight, ExternalLink,
} from 'lucide-react';
import { apiRequest } from '@/utils/api-request';
import Button from '@/components/ui/Button';
import SettingsSlideModal from '@/components/Modal/SettingsSlideModal';
import { showToast, ToastManager } from '@/components/ui/Toast';
import type {
  ExtractionSummary, ExtractionStatus, ExtractionReview, ApplySelections, SchoolSearchResult,
} from '@/types/websiteSync';

// ── Small display helpers ──────────────────────────────────────────────────

const STATUS_STYLES: Record<ExtractionStatus, { label: string; className: string; icon: React.ReactNode }> = {
  pending_review: {
    label: 'Pending Review',
    className: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
    icon: <Clock className="h-3 w-3" />,
  },
  partially_applied: {
    label: 'Partially Applied',
    className: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
    icon: <RefreshCw className="h-3 w-3" />,
  },
  approved: {
    label: 'Approved',
    className: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
    icon: <CheckCircle2 className="h-3 w-3" />,
  },
  rejected: {
    label: 'Rejected',
    className: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
    icon: <X className="h-3 w-3" />,
  },
};

function StatusBadge({ status }: { status: ExtractionStatus }) {
  const s = STATUS_STYLES[status];
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${s.className}`}>
      {s.icon}
      {s.label}
    </span>
  );
}

function fieldLabel(key: string) {
  return key
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

// A single checkbox row used throughout the review panel — a diff row with
// a current-vs-extracted comparison, checked by default (matches "omitting
// selections accepts everything" on the real apply endpoint).
function CheckableRow({
  label, extractedValue, checked, onChange,
}: { label: React.ReactNode; extractedValue: React.ReactNode; checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <label className="flex items-start gap-3 py-2 cursor-pointer group">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="mt-0.5 w-4 h-4 text-brandColor border-gray-300 rounded focus:ring-brandColor flex-shrink-0"
      />
      <div className="flex-1 min-w-0">
        <div className="text-xs font-medium text-gray-500 dark:text-gray-400">{label}</div>
        <div className="text-sm text-gray-900 dark:text-white break-words">{extractedValue}</div>
      </div>
    </label>
  );
}

// A row for a field/item that will NOT be written (already has a value, or
// already on file) — shown for transparency, never checkable.
function SkippedRow({ label, currentValue, reason }: { label: string; currentValue: React.ReactNode; reason: string }) {
  return (
    <div className="flex items-start gap-3 py-2 opacity-60">
      <div className="mt-0.5 w-4 h-4 flex-shrink-0 flex items-center justify-center">
        <div className="w-3 h-3 rounded-sm bg-gray-300 dark:bg-gray-600" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-xs font-medium text-gray-500 dark:text-gray-400">{label}</div>
        <div className="text-sm text-gray-600 dark:text-gray-400 break-words">{currentValue}</div>
        <div className="text-xs text-gray-400 dark:text-gray-500 italic mt-0.5">{reason}</div>
      </div>
    </div>
  );
}

// Read-only list for things nothing can be done with yet (unmapped mentions,
// overflow needing a manual decision, KB entries with no table to land in).
function ReadOnlyNotice({ title, items, tone = 'info' }: { title: string; items: string[]; tone?: 'info' | 'warning' }) {
  if (!items.length) return null;
  const toneClasses = tone === 'warning'
    ? 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800 text-amber-800 dark:text-amber-300'
    : 'bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400';
  return (
    <div className={`rounded-lg border p-3 mt-2 ${toneClasses}`}>
      <div className="flex items-center gap-1.5 text-xs font-semibold uppercase mb-1.5">
        <AlertTriangle className="h-3.5 w-3.5" />
        {title}
      </div>
      <ul className="text-sm space-y-0.5 list-disc list-inside">
        {items.map((item, i) => <li key={i}>{item}</li>)}
      </ul>
    </div>
  );
}

// Single-choice row (radio semantics) — used where only one candidate can ever
// land in a single-value column (e.g. one logo URL into school_info.logo_tmp).
function RadioRow({
  label, extractedValue, checked, onSelect,
}: { label: string; extractedValue: React.ReactNode; checked: boolean; onSelect: () => void }) {
  return (
    <label className="flex items-start gap-3 py-2 cursor-pointer group">
      <input
        type="radio"
        checked={checked}
        onChange={onSelect}
        className="mt-0.5 w-4 h-4 text-brandColor border-gray-300 focus:ring-brandColor flex-shrink-0"
      />
      <div className="flex-1 min-w-0">
        <div className="text-xs font-medium text-gray-500 dark:text-gray-400">{label}</div>
        <div className="text-sm text-gray-900 dark:text-white break-words">{extractedValue}</div>
      </div>
    </label>
  );
}

function SectionCard({ title, subtitle, children }: { title: string; subtitle?: string; children: React.ReactNode }) {
  return (
    <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
      <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">{title}</h4>
      {subtitle && <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">{subtitle}</p>}
      <div className="mt-2 divide-y divide-gray-100 dark:divide-gray-700">{children}</div>
    </div>
  );
}

// ── Checkbox selection state shape (mirrors ApplySelections, but as UI state
// keyed for easy checkbox binding rather than the compact API payload shape) ──

type MappingKey = 'sports' | 'activities' | 'labs' | 'medical' | 'mediums';

interface SelectionState {
  schoolInfoFields: Record<string, boolean>;
  board: boolean;
  collection: boolean;
  mapping: Record<MappingKey, Record<number, boolean>>;
  // Free-text unmapped mentions, keyed by the mention text itself (no id yet —
  // one doesn't exist until the row is created on apply).
  mappingNew: Record<MappingKey, Record<string, boolean>>;
  admissions: Record<string, boolean>; // keyed by grade text
  feeRanges: Record<number, boolean>;
  team: Record<number, boolean>;
  kbEntries: Record<number, boolean>;
  media: {
    ogImage: boolean; logo: string | null;
    youtube: Record<string, boolean>;
    galleryImages: Record<string, boolean>;
    documentLinks: Record<string, boolean>;
  };
}

const MAPPING_KEYS: MappingKey[] = ['sports', 'activities', 'labs', 'medical', 'mediums'];

function buildDefaultSelections(review: ExtractionReview): SelectionState {
  const { plan } = review;
  const schoolInfoFields: Record<string, boolean> = {};
  Object.keys(plan.school_info_updates).forEach((k) => { schoolInfoFields[k] = true; });
  // Overwrite candidates default OFF — the reviewer opts in to replace a value
  // that's already on file, instead of it being silently skipped or blocked.
  Object.keys(plan.school_info_overwrite_candidates).forEach((k) => { schoolInfoFields[k] = false; });

  const mapping = { sports: {}, activities: {}, labs: {}, medical: {}, mediums: {} } as Record<MappingKey, Record<number, boolean>>;
  MAPPING_KEYS.forEach((k) => {
    plan.mapping_inserts[k].forEach((item) => { mapping[k][item.id] = true; });
  });

  const mappingNew = { sports: {}, activities: {}, labs: {}, medical: {}, mediums: {} } as Record<MappingKey, Record<string, boolean>>;
  MAPPING_KEYS.forEach((k) => {
    plan.mapping_new_inserts[k].forEach((name) => { mappingNew[k][name] = true; });
  });

  const feeRanges: Record<number, boolean> = {};
  plan.fee_range_inserts.forEach((_, i) => { feeRanges[i] = true; });

  const team: Record<number, boolean> = {};
  plan.team_inserts.forEach((_, i) => { team[i] = true; });

  const admissions: Record<string, boolean> = {};
  plan.admissions_inserts.forEach((grade) => { admissions[grade] = true; });

  const kbEntries: Record<number, boolean> = {};
  plan.kb_entries_inserts.forEach((_, i) => { kbEntries[i] = true; });

  const youtube: Record<string, boolean> = {};
  plan.media.youtube_new.forEach((url) => { youtube[url] = true; });

  const galleryImages: Record<string, boolean> = {};
  plan.media.gallery_images_new.forEach((url) => { galleryImages[url] = true; });

  const documentLinks: Record<string, boolean> = {};
  plan.media.document_links_new.forEach((d) => { documentLinks[d.url] = true; });

  return {
    schoolInfoFields,
    board: !!plan.board_updates?.length,
    collection: !!plan.collection_updates?.length,
    mapping,
    mappingNew,
    admissions,
    feeRanges,
    team,
    kbEntries,
    media: {
      ogImage: !!plan.media.og_image && !plan.media.og_image.current_image_profile,
      logo: plan.media.logo_candidates.length
        ? (plan.media.logo_candidates.find((c) => !c.current_logo_tmp)?.url ?? null)
        : null,
      youtube,
      galleryImages,
      documentLinks,
    },
  };
}

function countSelected(review: ExtractionReview, sel: SelectionState): number {
  const { plan } = review;
  let n = Object.values(sel.schoolInfoFields).filter(Boolean).length;
  if (sel.board && plan.board_updates) n += plan.board_updates.length;
  if (sel.collection && plan.collection_updates) n += plan.collection_updates.length;
  MAPPING_KEYS.forEach((k) => {
    n += Object.values(sel.mapping[k]).filter(Boolean).length;
    n += Object.values(sel.mappingNew[k]).filter(Boolean).length;
  });
  n += Object.values(sel.admissions).filter(Boolean).length;
  n += Object.values(sel.feeRanges).filter(Boolean).length;
  n += Object.values(sel.team).filter(Boolean).length;
  n += Object.values(sel.kbEntries).filter(Boolean).length;
  if (sel.media.ogImage && plan.media.og_image) n += 1;
  if (sel.media.logo) n += 1;
  n += Object.values(sel.media.youtube).filter(Boolean).length;
  n += Object.values(sel.media.galleryImages).filter(Boolean).length;
  n += Object.values(sel.media.documentLinks).filter(Boolean).length;
  return n;
}

// Converts UI checkbox state into the compact payload the real /apply endpoint expects.
function toApplySelections(review: ExtractionReview, sel: SelectionState): ApplySelections {
  const { plan } = review;
  const mappingKey = (k: MappingKey) =>
    plan.mapping_inserts[k].filter((item) => sel.mapping[k][item.id]).map((item) => item.id);
  const mappingNewKey = (k: MappingKey) =>
    plan.mapping_new_inserts[k].filter((name) => sel.mappingNew[k][name]);

  return {
    school_info_fields: Object.keys(sel.schoolInfoFields).filter((k) => sel.schoolInfoFields[k]),
    board: sel.board,
    collection: sel.collection,
    mapping: {
      sports: mappingKey('sports'),
      activities: mappingKey('activities'),
      labs: mappingKey('labs'),
      medical: mappingKey('medical'),
      mediums: mappingKey('mediums'),
    },
    mapping_new: {
      sports: mappingNewKey('sports'),
      activities: mappingNewKey('activities'),
      labs: mappingNewKey('labs'),
      medical: mappingNewKey('medical'),
      mediums: mappingNewKey('mediums'),
    },
    admissions: plan.admissions_inserts.filter((g) => sel.admissions[g]),
    fee_ranges: plan.fee_range_inserts.map((_, i) => i).filter((i) => sel.feeRanges[i]),
    team: plan.team_inserts.map((_, i) => i).filter((i) => sel.team[i]),
    kb_entries: plan.kb_entries_inserts.map((_, i) => i).filter((i) => sel.kbEntries[i]),
    media: {
      og_image: sel.media.ogImage,
      logo: sel.media.logo || 'none',
      youtube: plan.media.youtube_new.filter((url) => sel.media.youtube[url]),
      gallery_images: plan.media.gallery_images_new.filter((url) => sel.media.galleryImages[url]),
      document_links: plan.media.document_links_new.filter((d) => sel.media.documentLinks[d.url]).map((d) => d.url),
    },
  };
}

// ── School picker (Run New Extraction) ─────────────────────────────────────

function SchoolPicker({
  onSelect,
}: { onSelect: (school: SchoolSearchResult) => void }) {
  const [term, setTerm] = useState('');
  const [open, setOpen] = useState(false);
  const [results, setResults] = useState<SchoolSearchResult[]>([]);
  const [searching, setSearching] = useState(false);
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleChange = (value: string) => {
    setTerm(value);
    setOpen(true);
    if (debounceTimer.current) clearTimeout(debounceTimer.current);

    if (value.trim().length < 2) {
      setResults([]);
      return;
    }

    debounceTimer.current = setTimeout(async () => {
      setSearching(true);
      try {
        const res = await apiRequest<{ schools: SchoolSearchResult[] }>('/api/schools/search', {
          method: 'POST',
          body: { search_term: value, limit: 20 },
        });
        setResults(res.success ? res.data?.schools || [] : []);
      } catch {
        setResults([]);
      } finally {
        setSearching(false);
      }
    }, 300);
  };

  return (
    <div className="relative">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
        <input
          type="text"
          value={term}
          onChange={(e) => handleChange(e.target.value)}
          onFocus={() => setOpen(true)}
          placeholder="Search school by name..."
          className="w-full pl-9 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-brandColor focus:border-transparent dark:bg-gray-700 dark:text-white text-sm"
        />
      </div>
      {open && searching && (
        <div className="absolute z-30 left-0 top-full mt-1 w-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md shadow-lg px-3 py-2 text-xs text-gray-400">
          Searching...
        </div>
      )}
      {open && !searching && results.length > 0 && (
        <div className="absolute z-30 left-0 top-full mt-1 w-full max-h-60 overflow-y-auto bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md shadow-lg">
          {results.map((school) => (
            <button
              key={school.slate_id}
              onClick={() => { onSelect(school); setTerm(''); setOpen(false); }}
              className="w-full text-left px-3 py-2 hover:bg-gray-50 dark:hover:bg-gray-700 border-b border-gray-100 dark:border-gray-700 last:border-b-0"
            >
              <div className="text-sm font-medium text-gray-900 dark:text-white">{school.slate_name}</div>
              <div className="text-xs text-gray-500 dark:text-gray-400">
                {school.area_name}, {school.city_name} · slate_id {school.slate_id}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Main page ───────────────────────────────────────────────────────────────

const WebsiteSyncAdmin: React.FC = () => {
  const [queue, setQueue] = useState<ExtractionSummary[]>([]);
  const [queueLoading, setQueueLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<'all' | ExtractionStatus>('all');

  const [selectedSchool, setSelectedSchool] = useState<SchoolSearchResult | null>(null);
  const [overrideUrl, setOverrideUrl] = useState('');
  const [isExtracting, setIsExtracting] = useState(false);

  const [activeExtractionId, setActiveExtractionId] = useState<number | null>(null);
  const [activeSlateId, setActiveSlateId] = useState<number | null>(null);
  const [activeReview, setActiveReview] = useState<ExtractionReview | null>(null);
  const [selections, setSelections] = useState<SelectionState | null>(null);
  const [isApplying, setIsApplying] = useState(false);
  const [reviewLoading, setReviewLoading] = useState(false);

  const loadQueue = async () => {
    setQueueLoading(true);
    try {
      const res = await apiRequest<{ extractions: ExtractionSummary[] }>('/api/website-sync/queue', { method: 'GET' });
      if (res.success) {
        setQueue(res.data?.extractions || []);
      } else {
        showToast(res.message || 'Failed to load extraction queue', 'error');
      }
    } catch {
      showToast('Failed to load extraction queue', 'error');
    } finally {
      setQueueLoading(false);
    }
  };

  useEffect(() => { loadQueue(); }, []);

  const filteredQueue = statusFilter === 'all' ? queue : queue.filter((q) => q.status === statusFilter);

  const pendingCount = queue.filter((q) => q.status === 'pending_review').length;

  const handleSelectSchool = (school: SchoolSearchResult) => {
    setSelectedSchool(school);
    setOverrideUrl(school.website || '');
  };

  const handleRunExtraction = async () => {
    if (!selectedSchool || !overrideUrl) return;
    setIsExtracting(true);
    try {
      const res = await apiRequest<{ extraction_id: number; slate_id: number }>(
        `/api/website-sync/${selectedSchool.slate_id}/extract`,
        { method: 'POST', body: { overrideUrl } }
      );
      if (!res.success || !res.data?.extraction_id) {
        showToast(res.message || 'Extraction failed', 'error');
        return;
      }
      showToast(`Extraction complete for ${selectedSchool.slate_name}`, 'success');
      const slateId = selectedSchool.slate_id;
      const extractionId = res.data.extraction_id;
      setSelectedSchool(null);
      setOverrideUrl('');
      await loadQueue();
      await openReview(slateId, extractionId);
    } catch {
      showToast('Extraction failed — check the server logs', 'error');
    } finally {
      setIsExtracting(false);
    }
  };

  const openReview = async (slateId: number, extractionId?: number) => {
    setReviewLoading(true);
    setActiveSlateId(slateId);
    setActiveExtractionId(extractionId ?? null);
    try {
      const qs = extractionId ? `?extraction_id=${extractionId}` : '';
      const res = await apiRequest<ExtractionReview>(`/api/website-sync/${slateId}${qs}`, { method: 'GET' });
      if (!res.success || !res.data) {
        showToast(res.message || 'Failed to load extraction review', 'error');
        setActiveSlateId(null);
        setActiveExtractionId(null);
        return;
      }
      const review = res.data;
      setActiveExtractionId(review.extraction.id);
      setActiveReview(review);
      setSelections(buildDefaultSelections(review));
    } catch {
      showToast('Failed to load extraction review', 'error');
      setActiveSlateId(null);
      setActiveExtractionId(null);
    } finally {
      setReviewLoading(false);
    }
  };

  const closeReview = () => {
    setActiveExtractionId(null);
    setActiveSlateId(null);
    setActiveReview(null);
    setSelections(null);
  };

  const handleApply = async () => {
    if (!activeReview || !selections || !activeSlateId || !activeExtractionId) return;
    const payload = toApplySelections(activeReview, selections);
    const changeCount = countSelected(activeReview, selections);
    if (changeCount === 0) {
      showToast('Nothing selected to apply', 'error');
      return;
    }
    setIsApplying(true);
    try {
      const res = await apiRequest<{ status: ExtractionStatus; applied_changes: number }>(
        `/api/website-sync/${activeSlateId}/apply`,
        { method: 'POST', body: { extraction_id: activeExtractionId, selections: payload } }
      );
      if (!res.success) {
        showToast(res.message || 'Apply failed', 'error');
        return;
      }
      showToast(`Applied ${res.data?.applied_changes ?? changeCount} change(s)`, 'success');
      await loadQueue();
      closeReview();
    } catch {
      showToast('Apply failed — check the server logs', 'error');
    } finally {
      setIsApplying(false);
    }
  };

  const toggleAll = (key: keyof SelectionState['mapping'], checked: boolean) => {
    if (!activeReview) return;
    setSelections((prev) => {
      if (!prev) return prev;
      const next = { ...prev.mapping[key] };
      activeReview.plan.mapping_inserts[key].forEach((item) => { next[item.id] = checked; });
      return { ...prev, mapping: { ...prev.mapping, [key]: next } };
    });
  };

  const toggleAllNew = (key: MappingKey, checked: boolean) => {
    if (!activeReview) return;
    setSelections((prev) => {
      if (!prev) return prev;
      const next = { ...prev.mappingNew[key] };
      activeReview.plan.mapping_new_inserts[key].forEach((name) => { next[name] = checked; });
      return { ...prev, mappingNew: { ...prev.mappingNew, [key]: next } };
    });
  };

  const selectedCount = activeReview && selections ? countSelected(activeReview, selections) : 0;

  return (
    <div className="h-full flex flex-col bg-gray-50 dark:bg-gray-900">
      <ToastManager />

      {/* Header */}
      <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-6 py-4">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <div className="bg-brandColor/10 p-2 rounded-lg">
              <Globe className="h-5 w-5 text-brandColor" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h1 className="text-lg font-semibold text-gray-900 dark:text-white">Website Sync</h1>
                {pendingCount > 0 && (
                  <span className="text-xs px-1.5 py-0.5 rounded-full font-medium bg-yellow-100 text-yellow-700">
                    {pendingCount} pending
                  </span>
                )}
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Crawl a school&apos;s website, extract structured profile data, and review before applying
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-auto p-6 space-y-6">
        {/* Run New Extraction */}
        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">Run New Extraction</h3>
          <div className="grid grid-cols-1 md:grid-cols-[2fr,2fr,auto] gap-3 items-start">
            <div>
              <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">School</label>
              {selectedSchool ? (
                <div className="flex items-center justify-between px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700">
                  <span className="text-sm text-gray-900 dark:text-white">{selectedSchool.slate_name}</span>
                  <button onClick={() => { setSelectedSchool(null); setOverrideUrl(''); }} className="text-gray-400 hover:text-gray-600">
                    <X className="h-4 w-4" />
                  </button>
                </div>
              ) : (
                <SchoolPicker onSelect={handleSelectSchool} />
              )}
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                Website URL {selectedSchool && <span className="italic font-normal">(editable override)</span>}
              </label>
              <input
                type="text"
                value={overrideUrl}
                onChange={(e) => setOverrideUrl(e.target.value)}
                disabled={!selectedSchool}
                placeholder="https://school-site.com"
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-brandColor focus:border-transparent dark:bg-gray-700 dark:text-white text-sm disabled:opacity-50 disabled:cursor-not-allowed"
              />
            </div>
            <div className="pt-5">
              <Button
                variant="brand"
                onClick={handleRunExtraction}
                disabled={!selectedSchool || !overrideUrl}
                loading={isExtracting}
              >
                Run Extraction
              </Button>
            </div>
          </div>
        </div>

        {/* Queue */}
        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between flex-wrap gap-2">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Extraction Queue</h3>
            <div className="flex items-center gap-1">
              {(['all', 'pending_review', 'partially_applied', 'approved'] as const).map((s) => (
                <button
                  key={s}
                  onClick={() => setStatusFilter(s)}
                  className={`px-2.5 py-1 rounded-md text-xs font-medium transition-colors ${
                    statusFilter === s
                      ? 'bg-brandColor text-white'
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                  }`}
                >
                  {s === 'all' ? 'All' : STATUS_STYLES[s].label}
                </button>
              ))}
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-brandColor text-white text-xs uppercase">
                  <th className="text-left px-4 py-2.5 font-medium">School</th>
                  <th className="text-left px-4 py-2.5 font-medium">Source URL</th>
                  <th className="text-left px-4 py-2.5 font-medium">Extracted</th>
                  <th className="text-left px-4 py-2.5 font-medium">Cost</th>
                  <th className="text-left px-4 py-2.5 font-medium">Status</th>
                  <th className="px-4 py-2.5" />
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                {queueLoading && (
                  <tr>
                    <td colSpan={6} className="px-4 py-8 text-center text-gray-400 text-sm">
                      Loading extractions...
                    </td>
                  </tr>
                )}
                {!queueLoading && filteredQueue.map((row) => (
                  <tr
                    key={row.id}
                    onClick={() => openReview(row.slate_id, row.id)}
                    className="hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer"
                  >
                    <td className="px-4 py-3">
                      <div className="font-medium text-gray-900 dark:text-white">{row.school_name}</div>
                      <div className="text-xs text-gray-400">slate_id {row.slate_id}</div>
                    </td>
                    <td className="px-4 py-3">
                      <a
                        href={row.source_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        className="text-brandColor hover:underline inline-flex items-center gap-1 text-xs"
                      >
                        {row.source_url.replace(/^https?:\/\//, '')}
                        <ExternalLink className="h-3 w-3" />
                      </a>
                    </td>
                    <td className="px-4 py-3 text-gray-500 dark:text-gray-400 text-xs">{row.created_at}</td>
                    <td className="px-4 py-3 text-gray-500 dark:text-gray-400 text-xs">${Number(row.estimated_cost_usd).toFixed(4)}</td>
                    <td className="px-4 py-3"><StatusBadge status={row.status} /></td>
                    <td className="px-4 py-3 text-right">
                      <ChevronRight className="h-4 w-4 text-gray-300 dark:text-gray-600 inline-block" />
                    </td>
                  </tr>
                ))}
                {!queueLoading && filteredQueue.length === 0 && (
                  <tr>
                    <td colSpan={6} className="px-4 py-8 text-center text-gray-400 text-sm">
                      No extractions match this filter.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Review panel */}
      <SettingsSlideModal
        isOpen={!!activeSlateId}
        onClose={closeReview}
        title={activeReview?.extraction.school_name || 'Loading...'}
        subtitle={activeReview && (
          <span>
            {activeReview.extraction.source_url} · extracted {activeReview.extraction.created_at} · $
            {Number(activeReview.extraction.estimated_cost_usd).toFixed(4)}
          </span>
        )}
        icon={<Globe className="h-5 w-5" />}
        width="720px"
      >
        {reviewLoading && (
          <div className="flex-1 flex items-center justify-center text-sm text-gray-400 py-16">
            Loading extraction review...
          </div>
        )}
        {!reviewLoading && activeReview && selections && (
          <>
            <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
              {/* Basic Info */}
              {(Object.keys(activeReview.plan.school_info_updates).length > 0 ||
                Object.keys(activeReview.plan.school_info_overwrite_candidates).length > 0) && (
                <SectionCard title="Basic Info" subtitle="school_info fields">
                  {Object.entries(activeReview.plan.school_info_updates).map(([key, value]) => (
                    <CheckableRow
                      key={key}
                      label={fieldLabel(key)}
                      extractedValue={value}
                      checked={!!selections.schoolInfoFields[key]}
                      onChange={(v) => setSelections((prev) => prev && ({
                        ...prev, schoolInfoFields: { ...prev.schoolInfoFields, [key]: v },
                      }))}
                    />
                  ))}
                  {Object.entries(activeReview.plan.school_info_overwrite_candidates).map(([key, c]) => (
                    <CheckableRow
                      key={key}
                      label={
                        <>
                          {`${fieldLabel(key)} (would replace: "${c.current}")`}
                          {c.shorter_than_current && (
                            <span className="ml-2 inline-block px-1.5 py-0.5 rounded text-[10px] font-semibold bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300 align-middle">
                              shorter than current — check before accepting
                            </span>
                          )}
                        </>
                      }
                      extractedValue={c.extracted}
                      checked={!!selections.schoolInfoFields[key]}
                      onChange={(v) => setSelections((prev) => prev && ({
                        ...prev, schoolInfoFields: { ...prev.schoolInfoFields, [key]: v },
                      }))}
                    />
                  ))}
                </SectionCard>
              )}

              {/* Boards & Collections */}
              {(activeReview.plan.board_updates || activeReview.plan.collection_updates ||
                activeReview.plan.board_overflow_skipped?.length || activeReview.plan.collection_overflow_skipped?.length) && (
                <SectionCard title="Boards & Collections">
                  {activeReview.plan.board_updates && (
                    <CheckableRow
                      label="Boards to add"
                      extractedValue={activeReview.plan.board_updates.map((b) => b.board_name).join(', ')}
                      checked={selections.board}
                      onChange={(v) => setSelections((prev) => prev && ({ ...prev, board: v }))}
                    />
                  )}
                  {activeReview.plan.collection_updates && (
                    <CheckableRow
                      label="Collections to add"
                      extractedValue={activeReview.plan.collection_updates.map((c) => c.collection_name).join(', ')}
                      checked={selections.collection}
                      onChange={(v) => setSelections((prev) => prev && ({ ...prev, collection: v }))}
                    />
                  )}
                  <ReadOnlyNotice title="Needs admin decision — more found than empty slots" items={(activeReview.plan.board_overflow_skipped || []).map((b) => b.name)} tone="warning" />
                  <ReadOnlyNotice title="Needs admin decision — more found than empty slots" items={(activeReview.plan.collection_overflow_skipped || []).map((c) => c.name)} tone="warning" />
                </SectionCard>
              )}

              {/* Sports / Activities / Labs / Medical / Mediums */}
              {MAPPING_KEYS.map((key) => {
                const items = activeReview.plan.mapping_inserts[key];
                const newItems = activeReview.plan.mapping_new_inserts[key];
                if (!items.length && !newItems.length) return null;
                return (
                  <SectionCard key={key} title={fieldLabel(key)}>
                    {items.length > 0 && (
                      <div className="flex items-center justify-between py-1.5">
                        <span className="text-xs text-gray-400">{items.length} matched to fixed list</span>
                        <button
                          onClick={() => toggleAll(key, !items.every((i) => selections.mapping[key][i.id]))}
                          className="text-xs text-brandColor hover:underline"
                        >
                          {items.every((i) => selections.mapping[key][i.id]) ? 'Deselect all' : 'Select all'}
                        </button>
                      </div>
                    )}
                    {items.map((item) => (
                      <CheckableRow
                        key={item.id}
                        label={fieldLabel(key).slice(0, -1)}
                        extractedValue={item.name}
                        checked={!!selections.mapping[key][item.id]}
                        onChange={(v) => setSelections((prev) => prev && ({
                          ...prev,
                          mapping: { ...prev.mapping, [key]: { ...prev.mapping[key], [item.id]: v } },
                        }))}
                      />
                    ))}
                    {newItems.length > 0 && (
                      <>
                        <div className="flex items-center justify-between py-1.5">
                          <span className="text-xs text-gray-400">{newItems.length} not in the fixed list — add as new</span>
                          <button
                            onClick={() => toggleAllNew(key, !newItems.every((n) => selections.mappingNew[key][n]))}
                            className="text-xs text-brandColor hover:underline"
                          >
                            {newItems.every((n) => selections.mappingNew[key][n]) ? 'Deselect all' : 'Select all'}
                          </button>
                        </div>
                        {newItems.map((name) => (
                          <CheckableRow
                            key={name}
                            label={`${fieldLabel(key).slice(0, -1)} (new)`}
                            extractedValue={name}
                            checked={!!selections.mappingNew[key][name]}
                            onChange={(v) => setSelections((prev) => prev && ({
                              ...prev,
                              mappingNew: { ...prev.mappingNew, [key]: { ...prev.mappingNew[key], [name]: v } },
                            }))}
                          />
                        ))}
                      </>
                    )}
                  </SectionCard>
                );
              })}

              {/* Admissions */}
              {(activeReview.plan.admissions_inserts.length > 0 ||
                activeReview.plan.admissions_already_present.length > 0 ||
                activeReview.plan.admissions_dropped_too_long.length > 0) && (
                <SectionCard title="Admissions" subtitle="school_admissions">
                  {activeReview.plan.admissions_inserts.map((grade) => (
                    <CheckableRow
                      key={grade}
                      label="Grade to insert"
                      extractedValue={grade}
                      checked={!!selections.admissions[grade]}
                      onChange={(v) => setSelections((prev) => prev && ({ ...prev, admissions: { ...prev.admissions, [grade]: v } }))}
                    />
                  ))}
                  {activeReview.plan.admissions_already_present.map((grade) => (
                    <SkippedRow key={grade} label="Grade" currentValue={grade} reason="already on file — skipped" />
                  ))}
                  <ReadOnlyNotice title="Dropped — over 15 characters" items={activeReview.plan.admissions_dropped_too_long || []} tone="warning" />
                </SectionCard>
              )}

              {/* Fee Ranges */}
              {activeReview.plan.fee_range_inserts.length > 0 && (
                <SectionCard title="Fee Ranges" subtitle="school_fee_ranges">
                  {activeReview.plan.fee_range_inserts.map((fr, i) => (
                    <CheckableRow
                      key={i}
                      label={`${fr.curriculum || 'Unknown curriculum'} · ${fr.grade_from} → ${fr.grade_to}`}
                      extractedValue={`₹${Number(fr.min_fee).toLocaleString('en-IN')} – ₹${Number(fr.max_fee).toLocaleString('en-IN')}`}
                      checked={!!selections.feeRanges[i]}
                      onChange={(v) => setSelections((prev) => prev && ({ ...prev, feeRanges: { ...prev.feeRanges, [i]: v } }))}
                    />
                  ))}
                </SectionCard>
              )}

              {/* Team */}
              {(activeReview.plan.team_inserts.length > 0 || activeReview.plan.team_skipped_existing.length > 0) && (
                <SectionCard title="Team" subtitle="school_team">
                  {activeReview.plan.team_inserts.map((member, i) => (
                    <CheckableRow
                      key={i}
                      label={member.designation || 'Team member'}
                      extractedValue={member.name}
                      checked={!!selections.team[i]}
                      onChange={(v) => setSelections((prev) => prev && ({ ...prev, team: { ...prev.team, [i]: v } }))}
                    />
                  ))}
                  {activeReview.plan.team_skipped_existing.map((name, i) => (
                    <SkippedRow key={i} label="Team member" currentValue={name} reason="already on file — skipped" />
                  ))}
                </SectionCard>
              )}

              {/* KB Entries */}
              {activeReview.plan.kb_table_ready ? (
                (activeReview.plan.kb_entries_inserts.length > 0 || activeReview.plan.kb_entries_already_present.length > 0) && (
                  <SectionCard title="Knowledge Base Entries" subtitle="school_kb_entries">
                    {activeReview.plan.kb_entries_inserts.map((entry, i) => (
                      <CheckableRow
                        key={i}
                        label={`${entry.category} · ${entry.title}`}
                        extractedValue={entry.content}
                        checked={!!selections.kbEntries[i]}
                        onChange={(v) => setSelections((prev) => prev && ({ ...prev, kbEntries: { ...prev.kbEntries, [i]: v } }))}
                      />
                    ))}
                    {activeReview.plan.kb_entries_already_present.map((title) => (
                      <SkippedRow key={title} label="Entry" currentValue={title} reason="already on file — skipped" />
                    ))}
                  </SectionCard>
                )
              ) : (
                activeReview.plan.kb_entries_pending_migration > 0 && (
                  <SectionCard title="Knowledge Base Entries">
                    <ReadOnlyNotice
                      title={`${activeReview.plan.kb_entries_pending_migration} entries extracted — can't be applied yet`}
                      items={['school_kb_entries table migration hasn\'t been run yet — these are captured in the extraction but have nowhere to be written until then.']}
                    />
                  </SectionCard>
                )
              )}

              {/* Media candidates */}
              {(activeReview.plan.media.og_image || activeReview.plan.media.logo_candidates.length > 0 ||
                activeReview.plan.media.youtube_new.length > 0 || activeReview.plan.media.youtube_already_present.length > 0 ||
                activeReview.plan.media.gallery_images_new.length > 0 || activeReview.plan.media.gallery_images_already_present.length > 0 ||
                activeReview.plan.media.document_links_new.length > 0 || activeReview.plan.media.document_links_already_present.length > 0) && (
                <SectionCard title="Media Candidates" subtitle="school_info logo/profile image, vertical_assets photos/videos/documents">
                  {activeReview.plan.media.og_image && (
                    <CheckableRow
                      label={activeReview.plan.media.og_image.current_image_profile ? 'OG Image (would replace current)' : 'OG Image'}
                      extractedValue={activeReview.plan.media.og_image.url}
                      checked={selections.media.ogImage}
                      onChange={(v) => setSelections((prev) => prev && ({ ...prev, media: { ...prev.media, ogImage: v } }))}
                    />
                  )}
                  {activeReview.plan.media.logo_candidates.map((c, i) => (
                    <RadioRow
                      key={i}
                      label={c.current_logo_tmp ? 'Logo candidate (would replace current)' : 'Logo candidate'}
                      extractedValue={c.url}
                      checked={selections.media.logo === c.url}
                      onSelect={() => setSelections((prev) => prev && ({ ...prev, media: { ...prev.media, logo: c.url } }))}
                    />
                  ))}
                  {activeReview.plan.media.logo_candidates.length > 0 && (
                    <button
                      onClick={() => setSelections((prev) => prev && ({ ...prev, media: { ...prev.media, logo: null } }))}
                      className="text-xs text-gray-400 hover:underline py-1"
                    >
                      Use neither
                    </button>
                  )}
                  {activeReview.plan.media.youtube_new.map((url) => (
                    <CheckableRow
                      key={url}
                      label="YouTube"
                      extractedValue={url}
                      checked={!!selections.media.youtube[url]}
                      onChange={(v) => setSelections((prev) => prev && ({ ...prev, media: { ...prev.media, youtube: { ...prev.media.youtube, [url]: v } } }))}
                    />
                  ))}
                  {activeReview.plan.media.youtube_already_present.map((url) => (
                    <SkippedRow key={url} label="YouTube" currentValue={url} reason="already linked — skipped" />
                  ))}
                  {activeReview.plan.media.gallery_images_new.length > 0 && (
                    <div className="py-2">
                      <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-2">
                        Gallery Images ({activeReview.plan.media.gallery_images_new.length} new) · vertical_assets asset_type=1
                      </div>
                      <div className="grid grid-cols-4 sm:grid-cols-6 gap-2">
                        {activeReview.plan.media.gallery_images_new.map((url) => (
                          <label key={url} className="relative cursor-pointer group block" title={url}>
                            <img
                              src={url}
                              alt=""
                              loading="lazy"
                              className={`w-full h-16 object-cover rounded border ${
                                selections.media.galleryImages[url]
                                  ? 'border-brandColor'
                                  : 'border-gray-200 dark:border-gray-700 opacity-50'
                              }`}
                            />
                            <input
                              type="checkbox"
                              checked={!!selections.media.galleryImages[url]}
                              onChange={(e) => setSelections((prev) => prev && ({
                                ...prev,
                                media: { ...prev.media, galleryImages: { ...prev.media.galleryImages, [url]: e.target.checked } },
                              }))}
                              className="absolute top-1 left-1 w-4 h-4 text-brandColor border-gray-300 rounded focus:ring-brandColor"
                            />
                          </label>
                        ))}
                      </div>
                    </div>
                  )}
                  {activeReview.plan.media.gallery_images_already_present.length > 0 && (
                    <div className="text-xs text-gray-400 dark:text-gray-500 italic py-1">
                      {activeReview.plan.media.gallery_images_already_present.length} image(s) already on file — skipped
                    </div>
                  )}
                  {activeReview.plan.media.document_links_new.length > 0 && (
                    <div className="pt-2">
                      <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                        Compliance / Drive Documents · vertical_assets asset_type=3
                      </div>
                      {activeReview.plan.media.document_links_new.map((doc) => (
                        <CheckableRow
                          key={doc.url}
                          label={doc.label}
                          extractedValue={
                            <a href={doc.url} target="_blank" rel="noreferrer" className="text-brandColor hover:underline inline-flex items-center gap-1">
                              {doc.url} <ExternalLink className="h-3 w-3" />
                            </a>
                          }
                          checked={!!selections.media.documentLinks[doc.url]}
                          onChange={(v) => setSelections((prev) => prev && ({
                            ...prev,
                            media: { ...prev.media, documentLinks: { ...prev.media.documentLinks, [doc.url]: v } },
                          }))}
                        />
                      ))}
                    </div>
                  )}
                  {activeReview.plan.media.document_links_already_present.map((url) => (
                    <SkippedRow key={url} label="Document" currentValue={url} reason="already linked — skipped" />
                  ))}
                </SectionCard>
              )}

              {activeReview.total_planned_changes === 0 && (
                <div className="text-center py-8 text-gray-400 text-sm">
                  Nothing new to apply — everything extracted already matches live data.
                </div>
              )}

              {/* Pages Crawled — transparency into what actually fed this extraction */}
              {activeReview.pages_crawled.length > 0 && (
                <details className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                  <summary className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide cursor-pointer">
                    Pages Crawled ({activeReview.pages_crawled.length})
                  </summary>
                  <div className="mt-2 divide-y divide-gray-100 dark:divide-gray-700">
                    {activeReview.pages_crawled.map((p) => (
                      <div key={p.url} className="flex items-center justify-between gap-3 py-1.5 text-xs">
                        <a href={p.url} target="_blank" rel="noopener noreferrer" className="text-brandColor hover:underline truncate">
                          {p.url}
                        </a>
                        <span className="text-gray-400 flex-shrink-0">{p.method} · {p.charCount} chars</span>
                      </div>
                    ))}
                  </div>
                </details>
              )}
            </div>

            {/* Footer */}
            {activeReview.total_planned_changes > 0 && (
              <div className="border-t border-gray-200 dark:border-gray-700 px-6 py-4 flex items-center justify-between">
                <span className="text-sm text-gray-500 dark:text-gray-400">{selectedCount} change(s) selected</span>
                <div className="flex gap-2">
                  <Button variant="ghost" onClick={closeReview}>Cancel</Button>
                  <Button variant="brand" onClick={handleApply} loading={isApplying} disabled={selectedCount === 0}>
                    Apply Selected
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </SettingsSlideModal>
    </div>
  );
};

export default WebsiteSyncAdmin;
